import { useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { AnimatePresence, motion, useReducedMotion, type Transition, type Variants } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { Skeleton } from '@renderer/components/ui/skeleton'
import type { WritingStyleExcerpt } from '../use-writing-style-excerpts'
import { WritingStyleIcon } from './writing-style-icon'

const CYCLE_INTERVAL_MS = 4000
const STACK_DEPTH = 5
const LOADING_DEPTH = 3

const cardTransition: Transition = { type: 'spring', stiffness: 260, damping: 30 }
const contentTransition: Transition = { duration: 0.25, ease: 'easeOut' }

const cardVariants: Variants = {
  enter: { x: 36, y: 30, opacity: 0, filter: 'blur(3px)' },
  stacked: (depth: number) => ({
    x: depth * 12,
    y: depth * 10,
    opacity: 1 - depth * 0.3,
    filter: `blur(${depth}px)`,
    transition: cardTransition,
  }),
  // Only the front card is dealt away; cards dropped from further back (e.g. surplus loading placeholders) just fade.
  exit: (depth: number) =>
    depth === 0
      ? { x: -12, y: -24, opacity: 0, filter: 'blur(2px)', transition: { duration: 0.35, ease: 'easeIn' } }
      : { opacity: 0, transition: { duration: 0.2 } },
}

interface WritingStyleCardStackProps {
  excerpts: Array<WritingStyleExcerpt>
  /** Shows placeholder cards that fill in place once the excerpts arrive. */
  isLoading?: boolean
  className?: string
}

/** Learned writing styles as a stack of cards that deals the front card away on a loop. */
export function WritingStyleCardStack({
  excerpts,
  isLoading = false,
  className,
}: WritingStyleCardStackProps): ReactElement {
  const [position, setPosition] = useState(0)
  const prefersReducedMotion = useReducedMotion()
  const canCycle = !isLoading && excerpts.length > 1 && !prefersReducedMotion

  useEffect(() => {
    if (!canCycle) {
      return
    }
    const interval = setInterval(() => setPosition((current) => current + 1), CYCLE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [canCycle])

  // Keyed by absolute position so a style that cycles back around re-enters as a new card at the back,
  // and so the loading placeholders are the same cards that later show the excerpts.
  const slotCount = isLoading ? LOADING_DEPTH : Math.min(excerpts.length, STACK_DEPTH)
  const slots = Array.from({ length: slotCount }, (_, depth) => {
    const key = position + depth
    return { key, depth, excerpt: isLoading ? undefined : excerpts[key % excerpts.length] }
  })

  return (
    <div className={twMerge('relative isolate h-28', className)} aria-busy={isLoading}>
      <AnimatePresence initial={false}>
        {slots.map(({ key, depth, excerpt }) => (
          <motion.div
            key={key}
            custom={depth}
            variants={cardVariants}
            initial="enter"
            animate="stacked"
            exit="exit"
            style={{ zIndex: -key }}
            className="absolute inset-0"
            aria-hidden={depth > 0}
          >
            <WritingStyleCard excerpt={excerpt} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}

/** Renders placeholders until `excerpt` arrives, then fades the real content in over them. */
function WritingStyleCard({ excerpt }: { excerpt?: WritingStyleExcerpt }): ReactElement {
  return (
    <div className="bg-background flex h-fit gap-2.5 rounded-2xl px-3 py-2.5 shadow-md">
      <div className="flex shrink-0 flex-col">
        <div className="relative flex size-6 items-center justify-center rounded-lg border">
          <ContentFade isLoaded={!!excerpt}>
            {excerpt ? (
              <WritingStyleIcon integration={excerpt.integration} size={14} />
            ) : (
              <Skeleton className="size-3.5 rounded" />
            )}
          </ContentFade>
        </div>
        <span className="ml-3 h-[32px] w-2.5 rounded-bl-md border-b border-l" />
      </div>
      <div className="relative min-w-0 flex-1">
        <ContentFade isLoaded={!!excerpt} className="flex flex-col gap-1">
          {excerpt ? (
            <>
              <p className="text-foreground line-clamp-2 text-sm font-medium leading-normal">{excerpt.text}</p>
              <p className="text-foreground-placeholder truncate text-[12px] font-medium leading-[18px]">
                {excerpt.integration.name} Writing Style
              </p>
            </>
          ) : (
            <>
              {/* Rows match the loaded text's line heights so the card doesn't resize when it fills in. */}
              <div className="flex h-[42px] flex-col justify-around">
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-3/5" />
              </div>
              <div className="flex h-[18px] items-center">
                <Skeleton className="h-3 w-28" />
              </div>
            </>
          )}
        </ContentFade>
      </div>
    </div>
  )
}

interface ContentFadeProps {
  isLoaded: boolean
  className?: string
  children: ReactNode
}

/** Crossfades placeholder and loaded content in place; the outgoing layer is lifted out of flow while it fades. */
function ContentFade({ isLoaded, className, children }: ContentFadeProps): ReactElement {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      <motion.div
        key={isLoaded ? 'loaded' : 'loading'}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={contentTransition}
        className={className}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  )
}
